/**
 * Design tokens extraídos do Ofative UI Kit — 1:1, sem interpolação inventada.
 *
 * Fonte: https://www.figma.com/design/9Sm52kRIS9g20MTAyqe0Ms/Ofative-UI-KIT
 *
 * Extraído em 2026-09-06 via REST (`/v1/files/:key/nodes` + PNG @2x):
 *   ⚒️ Guideline → Tokens (62:13620) — Base Colors com hex publicado
 *   ⚒️ Guideline → Typography (62:13350)
 *   🔥 Screen → Login (135:33488) — 1440×960, form 551, hero 589
 *   🔥 Screen → Ofative (58:2316) — 1440×960, sidebar 288, header 80
 */

export type TokenProvenance = "figma" | "placeholder";

export interface TokenGroup<T> {
  provenance: TokenProvenance;
  /** Nome do frame/página do Figma de onde o grupo veio, quando aplicável. */
  source: string | null;
  values: T;
}

function placeholder<T>(values: T): TokenGroup<T> {
  return { provenance: "placeholder", source: null, values };
}

function fromFigma<T>(source: string, values: T): TokenGroup<T> {
  return { provenance: "figma", source, values };
}

/**
 * Paleta da Guideline → Base Colors e do painel direito / Sign In da Login.
 *
 * Só entram hexes que o kit nomeia ou que foram amostrados num preenchimento
 * sólido (hero `#0074FF`). Não há rampa 100–400 no arquivo: hover/pressed usam
 * `color-mix` no CSS a partir de `brand.500`, sem fingir swatches.
 */
export const colors = fromFigma("Guideline/Tokens/Base Colors + Screen/Login", {
  brand: {
    50: "#D0E5FF",
    100: "#D0E5FF",
    200: "#D0E5FF",
    300: "#D0E5FF",
    400: "#0074FF",
    500: "#0074FF",
    600: "#0074FF",
    700: "#0074FF",
    800: "#0074FF",
    900: "#4F5B80",
    950: "#4F5B80",
  },
  neutral: {
    0: "#FFFFFF",
    50: "#FBFBFC",
    100: "#F5F5F5",
    200: "#F5F3F3",
    300: "#E7E7EC",
    400: "#8E8E8E",
    500: "#61656E",
    600: "#4F5B80",
    700: "#475467",
    800: "#1C1C1C",
    900: "#1C1C1C",
    950: "#1C1C1C",
  },
  success: {
    50: "#EDF9E9",
    100: "#AFD4A5",
    500: "#51A13B",
    600: "#4A9336",
    700: "#4A9336",
  },
  warning: {
    50: "#FEF7E8",
    100: "#F9CE7A",
    200: "#F7B940",
    500: "#F5AB19",
    600: "#F0BE4C",
    700: "#AC7812",
  },
  danger: {
    50: "#FEEDED",
    100: "#F99696",
    200: "#F66969",
    500: "#F44A4A",
    600: "#F66969",
    700: "#AB3434",
  },
  info: {
    50: "#D0E5FF",
    100: "#D0E5FF",
    500: "#0074FF",
    600: "#0074FF",
    700: "#4F5B80",
  },
  labels: {
    gray: "#FBFBFC",
    gray2: "#F5F3F3",
    orange: "#F9DCD3",
    blue: "#D0E5FF",
    greenlight: "#DBECDD",
    green: "#BFDEDD",
    purple: "#E6DBF4",
    icon: "#4F5B80",
  },
});

/**
 * Tipografia da tabela "Atom: Typography" (Guideline).
 *
 * Letter spacing 0% em todos os estilos. A face de produto é Vend Sans (OFL,
 * hospedada em `packages/ui/src/fonts`). O kit Ofative usa Plus Jakarta Sans no
 * canvas; a troca é decisão de produto, não interpolação do Figma.
 *
 * Heading 1  28px / 120%
 * Heading 2  24px / 135%
 * Heading 3  20px / Auto
 * Heading 4  18px / 135%
 * Heading 5  16px / 150%
 * Body       14px / Auto
 * Caption    12px / 150%
 */
export const typography = fromFigma("Guideline/Typography/Atom: Typography", {
  fontFamily: {
    sans: '"Vend Sans", Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    mono: "ui-monospace, SFMono-Regular, Menlo, monospace",
  },
  fontSize: {
    xs: "0.75rem",
    sm: "0.875rem",
    base: "0.875rem",
    md: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    "2xl": "1.5rem",
    "3xl": "1.75rem",
    "4xl": "1.75rem",
  },
  lineHeight: {
    xs: "1.125rem",
    sm: "normal",
    base: "normal",
    md: "1.5rem",
    lg: "1.51875rem",
    xl: "normal",
    "2xl": "2.025rem",
    "3xl": "2.1rem",
    "4xl": "2.1rem",
  },
  fontWeight: {
    normal: "400",
    medium: "500",
    semibold: "600",
    bold: "700",
  },
  letterSpacing: {
    tight: "0",
    normal: "0",
    wide: "0",
  },
});

/**
 * Grade de 8px observada nas frames Login e Ofative (padding de formulário,
 * gap entre campos, gutters da sidebar e da grade semanal).
 */
export const spacing = fromFigma("Screen/Login + Screen/Ofative", {
  0: "0",
  px: "1px",
  0.5: "0.125rem",
  1: "0.25rem",
  1.5: "0.375rem",
  2: "0.5rem",
  2.5: "0.625rem",
  3: "0.75rem",
  4: "1rem",
  5: "1.25rem",
  6: "1.5rem",
  8: "2rem",
  10: "2.5rem",
  12: "3rem",
  16: "4rem",
  20: "5rem",
});

/** Inputs/botões da Login: 8px. Cards de evento da Ofative: 12px. */
export const radii = fromFigma("Screen/Login + Screen/Ofative", {
  none: "0",
  sm: "0.375rem",
  base: "0.5rem",
  md: "0.5rem",
  lg: "0.75rem",
  xl: "1rem",
  "2xl": "1.25rem",
  full: "9999px",
});

/**
 * Sombras amostradas nos effects das frames Screen (não há board de sombra).
 *
 *   xs  — botão do header Ofative (`Button Icon`): y1 r3 / ring 1px
 *   sm  — input do drawer Add Event
 *   md  — menus flutuantes (View options, Search, Edit event, Availability)
 *   lg  — drawer Add Event (`Card`, offset x −6)
 *   xl  — pill “upcoming event popup”
 *   focus — anel de foco da Login (`#0074FF` 28%)
 */
export const shadows = fromFigma("Screen/Ofative + Add Event + View options + Search", {
  none: "none",
  xs: "0 1px 3px rgb(18 18 18 / 0.10), 0 0 0 1px rgb(18 18 18 / 0.07)",
  sm: "0 1px 2px rgb(16 24 40 / 0.05), 0 0 0 1px rgb(18 18 18 / 0.10)",
  md: "0 4px 6px -2px rgb(16 24 40 / 0.03), 0 12px 16px -4px rgb(16 24 40 / 0.08)",
  lg: "-6px 0 24px -2px rgb(71 84 103 / 0.08)",
  xl: "0 4px 20px rgb(0 0 0 / 0.08)",
  focus: "0 0 0 3px rgb(0 116 255 / 0.28)",
});

/**
 * Chrome da frame Ofative 58:2316: sidebar 288px `#F5F5F5`, header 80px.
 */
export const layout = fromFigma("Screen/Ofative", {
  sidebarWidth: "288px",
  sidebarCollapsedWidth: "80px",
  topbarHeight: "80px",
  containerMaxWidth: "100%",
  gridColumns: 12,
  gridGutter: "1rem",
  breakpoints: {
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
    "2xl": "1536px",
  },
});

export const motion = placeholder({
  duration: {
    instant: "80ms",
    fast: "140ms",
    base: "200ms",
    exit: "160ms",
    slow: "320ms",
  },
  easing: {
    standard: "cubic-bezier(0.2, 0, 0.2, 1)",
    decelerate: "cubic-bezier(0, 0, 0.2, 1)",
    accelerate: "cubic-bezier(0.4, 0, 1, 1)",
  },
});

/**
 * Status mapeados 1:1 para os labels pastel e as escalas Warning/Error da
 * Guideline (não há tokens de status de entrega no kit — o mapeamento é o
 * único lugar em que o produto Dream Calendar escolhe qual swatch usar).
 */
export const statusColors = fromFigma("Guideline/Tokens/Base Colors", {
  planned: { bg: "#D0E5FF", fg: "#1E3A6E", border: "#9CC2F0", dot: "#0074FF" },
  in_production: {
    bg: "#D0E5FF",
    fg: "#1E3A6E",
    border: "#9CC2F0",
    dot: "#0074FF",
  },
  in_review: {
    bg: "#FEF7E8",
    fg: "#8A5A00",
    border: "#E4B25A",
    dot: "#AC7812",
  },
  approved: { bg: "#DBECDD", fg: "#1F4D28", border: "#9EC9A8", dot: "#4A9336" },
  scheduled: {
    bg: "#E6DBF4",
    fg: "#3D2A5C",
    border: "#C4B3E0",
    dot: "#6B4FA0",
  },
  published: {
    bg: "#BFDEDD",
    fg: "#1A4A48",
    border: "#7BB8B6",
    dot: "#2F7A72",
  },
  completed: {
    bg: "#DBECDD",
    fg: "#1F4D28",
    border: "#9EC9A8",
    dot: "#4A9336",
  },
  late: { bg: "#FEEDED", fg: "#8F1F1F", border: "#E07A7A", dot: "#F44A4A" },
  cancelled: {
    bg: "#E7E7EC",
    fg: "#3F3F46",
    border: "#C9C9D1",
    dot: "#52525B",
  },
});

export const priorityColors = fromFigma("Guideline/Tokens/Base Colors", {
  low: { fg: "#3F3F46", bg: "#E7E7EC", border: "#C9C9D1" },
  medium: { fg: "#1E3A6E", bg: "#D0E5FF", border: "#9CC2F0" },
  high: { fg: "#8A3B22", bg: "#F9DCD3", border: "#E8A090" },
  urgent: { fg: "#8F1F1F", bg: "#FEEDED", border: "#F99696" },
});

export const ALL_TOKEN_GROUPS = {
  colors,
  typography,
  spacing,
  radii,
  shadows,
  layout,
  motion,
  statusColors,
  priorityColors,
} as const;

export interface TokenAudit {
  complete: boolean;
  fromFigma: string[];
  pending: string[];
}

/**
 * Estado da extração do Figma.
 *
 * Consumido por `verify:tokens` e exibido na UI administrativa: o briefing
 * proíbe esconder funcionalidade incompleta, então a pendência aparece no
 * produto em vez de ficar só num documento.
 */
export function auditTokens(): TokenAudit {
  const fromFigma: string[] = [];
  const pending: string[] = [];

  for (const [name, group] of Object.entries(ALL_TOKEN_GROUPS)) {
    if ((group as TokenGroup<unknown>).provenance === "figma") fromFigma.push(name);
    else pending.push(name);
  }

  return { complete: pending.length === 0, fromFigma, pending };
}

export const tokens = {
  colors: colors.values,
  typography: typography.values,
  spacing: spacing.values,
  radii: radii.values,
  shadows: shadows.values,
  layout: layout.values,
  motion: motion.values,
  statusColors: statusColors.values,
  priorityColors: priorityColors.values,
} as const;

export type Tokens = typeof tokens;
